interface PasswordStrengthProps {
  /** The current password value. */
  value: string;
  /** Minimum length policy enforced by the form. */
  minLength: number;
  /** When true, the checklist renders even on empty input so the layout
   *  doesn't shift the moment the candidate starts typing. */
  alwaysVisible?: boolean;
  /** Optional email so we can also show the "no email in password" rule
   *  with live feedback. */
  email?: string;
}

interface Rule {
  key: string;
  label: string;
  test: (value: string, opts: { minLength: number; email?: string }) => boolean;
}

const RULES: Rule[] = [
  {
    key: 'length',
    label: 'At least {minLength} characters',
    test: (v, { minLength }) => v.length >= minLength,
  },
  {
    key: 'lower',
    label: 'A lowercase letter',
    test: (v) => /[a-z]/.test(v),
  },
  {
    key: 'upper',
    label: 'An uppercase letter',
    test: (v) => /[A-Z]/.test(v),
  },
  {
    key: 'digit',
    label: 'A digit',
    test: (v) => /[0-9]/.test(v),
  },
  {
    key: 'symbol',
    label: 'A symbol (e.g. ! @ # $ % &)',
    test: (v) => /[^A-Za-z0-9\s]/.test(v),
  },
  {
    key: 'nospace',
    label: 'No spaces or whitespace',
    test: (v) => v.length === 0 || !/\s/.test(v),
  },
];

const EMAIL_RULE: Rule = {
  key: 'noemail',
  label: 'Does not contain your email',
  test: (v, { email }) => {
    if (!email) return true;
    const local = email.split('@', 1)[0].trim().toLowerCase();
    if (local.length < 4) return true;
    return !v.toLowerCase().includes(local);
  },
};

/** Live, rule-by-rule checklist that mirrors the server-side password
 *  policy enforced by prism.candidates.service._validate_password_strength.
 *  Each rule shows green when satisfied and grey when still pending.
 *  Empty input shows all rules in the pending state. */
export function PasswordStrength({
  value,
  minLength,
  alwaysVisible = false,
  email,
}: PasswordStrengthProps) {
  if (value.length === 0 && !alwaysVisible) return null;

  const rules = email ? [...RULES, EMAIL_RULE] : RULES;
  const opts = { minLength, email };

  return (
    <div className="mt-2" role="status" aria-live="polite">
      <ul className="space-y-1 font-sans text-[12.5px] leading-snug">
        {rules.map((rule) => {
          const ok = rule.test(value, opts);
          const label = rule.label.replace('{minLength}', String(minLength));
          return (
            <li
              key={rule.key}
              className={[
                'flex items-start gap-2',
                ok
                  ? 'text-[var(--color-success-ink)]'
                  : 'text-[var(--color-ink-soft)]',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className={[
                  'mt-[2px] inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full border text-[10px] font-bold transition-colors duration-150',
                  ok
                    ? 'border-[var(--color-success)] bg-[var(--color-success)] text-white'
                    : 'border-[var(--color-rule-strong)] bg-[var(--color-surface)] text-[var(--color-ink-soft)]',
                ].join(' ')}
              >
                {ok ? '✓' : '·'}
              </span>
              <span>{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Pure helper — same rules, returned as a boolean. The setup form
 *  uses this to gate the submit button so the candidate can't even
 *  POST a non-compliant password (the server still re-validates). */
export function passwordMeetsPolicy(
  value: string,
  minLength: number,
  email?: string,
): boolean {
  const opts = { minLength, email };
  const rules = email ? [...RULES, EMAIL_RULE] : RULES;
  return rules.every((rule) => rule.test(value, opts));
}
